
import React, { useState, useEffect, useCallback, useRef } from "react";
import Axios from "../../_services/caller.service";
import styles from "./RemindersLateCalibration.module.scss";
import Modal from "../UiComponents/modals/Modal";
import InputForms from "../UiComponents/forms/InputForms";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";

const ReminderLateCalibration = ({setLoadingDataReminders}) => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState([]);

  const [error, setError] = useState(null);
  const [emailStatuses, setEmailStatuses] = useState({});
  const [searchTerm, setSearchTerm] = useState(""); // Recherche
  const [filterReminderType, setFilterReminderType] = useState("all"); // Filtre type de relance
  const [filterStatus, setFilterStatus] = useState("all"); // Filtre statut email
  const [sortField, setSortField] = useState(null); // Tri
  const [sortOrder, setSortOrder] = useState("asc");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Pour récupérer le profil utilisateur
  const [mode, setMode] = useState(null); // mode reminders : "manual" | "auto"
  const [cleaning, setCleaning] = useState(false); // nettoyage en cours
  const [refreshingStatuses, setRefreshingStatuses] = useState(false); // rafraîchissement des statuts email
  const [refreshProgress, setRefreshProgress] = useState(null); // { processed, total } pendant le rafraîchissement
  const progressPollRef = useRef(null); // id de l'intervalle de polling de progression

  const fetchReminders = useCallback(async () => {
    try {
      const response = await Axios.get("/getAllReminders", {
        params: { _ts: Date.now() },
      });
      const remindersData = response.data.data;

      setReminders(remindersData);
      setLoadingDataReminders(false)

      // Afficher le statut DÉJÀ stocké en base (reminder.status). C'est la
      // source fiable et persistée. Le rafraîchissement depuis Brevo se fait
      // côté serveur (bouton "Rafraîchir les statuts") pour ne pas saturer
      // l'API Brevo (429 Too Many Requests) depuis chaque navigateur.
      setEmailStatuses((prev) => {
        const next = { ...prev };
        remindersData.forEach((r) => {
          const key = r.messageId || r.id;
          if (!r.messageId) {
            next[key] = "No messageId";
          } else if (r.status && r.status !== "null" && r.status !== "Error") {
            next[key] = r.status;
          } else {
            next[key] = "waiting";
          }
        });
        return next;
      });

    } catch (error) {
      setError(t("reminders.error_loading"));

    }
  }, [setLoadingDataReminders, t]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // Récupère le mode (manuel/auto) pour conditionner le bouton de nettoyage
  useEffect(() => {
    Axios.get("/getConfig")
      .then((res) => setMode(res.data?.data?.mode ?? res.data?.mode ?? null))
      .catch(() => setMode(null));
  }, []);

  // Supprime les relances dont la calibration est repassée à jour (comme le CRON)
  const handleCleanObsolete = async () => {
    setCleaning(true);
    try {
      const response = await Axios.post("/cleanObsoleteReminders");
      if (response.data?.success) {
        const removed = response.data.removedCount ?? 0;
        const removedRows = response.data.removed ?? [];
        if (removed > 0) {
          toast.success(t("reminders.clean_success", { count: removed }));
          // Retrait immédiat des lignes supprimées (ne dépend pas du cache HTTP
          // du GET /getAllReminders, qui pouvait renvoyer l'ancienne liste).
          const removedIds = new Set(removedRows.map((r) => r.id));
          setReminders((prev) => prev.filter((r) => !removedIds.has(r.id)));
        } else {
          toast.info(t("reminders.clean_none"));
        }
        await fetchReminders();
      } else {
        toast.error(t("reminders.clean_error"));
      }
    } catch (error) {
      console.error("Erreur lors du nettoyage des relances :", error.message);
      toast.error(t("reminders.clean_error"));
    } finally {
      setCleaning(false);
      hideModal();
    }
  };

  // Déclenche le rafraîchissement serveur de tous les statuts email depuis
  // Brevo (séquentiel + throttle côté serveur), puis recharge la liste.
  // Au préalable, supprime les relances dont la calibration est repassée à
  // jour (même logique que le bouton "Rafraîchir la liste").
  const handleRefreshStatuses = async () => {
    setRefreshingStatuses(true);
    try {
      // 1) Nettoyage des relances obsolètes (calibrations à jour).
      try {
        const cleanRes = await Axios.post("/cleanObsoleteReminders");
        const removed = cleanRes.data?.removedCount ?? 0;
        const removedRows = cleanRes.data?.removed ?? [];
        if (removed > 0) {
          const removedIds = new Set(removedRows.map((r) => r.id));
          setReminders((prev) => prev.filter((r) => !removedIds.has(r.id)));
          toast.success(t("reminders.clean_success", { count: removed }));
        }
      } catch (cleanError) {
        console.error(
          "Erreur lors du nettoyage des relances :",
          cleanError.message
        );
      }

      // 2) Rafraîchissement des statuts email.
      const response = await Axios.post("/refreshAllEmailStatuses");
      if (response.data?.success) {
        toast.info(t("reminders.refresh_statuses_started"));
        // Le job tourne en arrière-plan côté serveur : on interroge
        // régulièrement l'avancement pour afficher une barre de progression.
        setRefreshProgress({ processed: 0, total: 0 });
        startProgressPolling();
      } else {
        toast.error(t("reminders.refresh_statuses_error"));
        setRefreshingStatuses(false);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.info(t("reminders.refresh_statuses_running"));
      } else {
        console.error("Erreur rafraîchissement statuts :", error.message);
        toast.error(t("reminders.refresh_statuses_error"));
      }
      setRefreshingStatuses(false);
    }
  };

  // Interroge l'endpoint de progression toutes les 2s. Met à jour la barre et,
  // une fois le job terminé côté serveur, recharge la liste et notifie.
  const startProgressPolling = () => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
    }
    progressPollRef.current = setInterval(async () => {
      try {
        const res = await Axios.get("/refreshStatusProgress", {
          params: { _ts: Date.now() },
        });
        const progress = res.data?.progress;
        if (!progress) return;

        setRefreshProgress({
          processed: progress.processed ?? 0,
          total: progress.total ?? 0,
        });

        // Le serveur a fini (running passé à false et un démarrage a eu lieu).
        if (!progress.running && progress.finishedAt) {
          clearInterval(progressPollRef.current);
          progressPollRef.current = null;
          await fetchReminders();
          toast.success(
            t("reminders.refresh_statuses_done", {
              updated: progress.updated ?? 0,
              total: progress.total ?? 0,
            })
          );
          setRefreshProgress(null);
          setRefreshingStatuses(false);
        }
      } catch (pollError) {
        console.error(
          "Erreur lors du suivi de progression :",
          pollError.message
        );
      }
    }, 2000);
  };

  // Nettoyage de l'intervalle si le composant est démonté en cours de job.
  useEffect(() => {
    return () => {
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
      }
    };
  }, []);

  // Gestion du filtrage
  const filteredReminders = reminders
    .filter((reminder) => {
      const searchText = searchTerm.toLowerCase();
      return (
       reminder.user_email && reminder.user_email.toLowerCase().includes(searchText) ||
        reminder.owner_login && reminder.owner_login.toLowerCase().includes(searchText) ||
        reminder.cabname && reminder.cabname.toLowerCase().includes(searchText)
      );
    })
    .filter((reminder) => {
      if (filterReminderType !== "all") {
        return reminder.reminderType === filterReminderType;
      }
      return true;
    })
    .filter((reminder) => {
      if (filterStatus !== "all") {
        return emailStatuses[reminder.messageId] === filterStatus;
      }
      return true;
    });

  // Gestion du tri
  const sortedReminders = [...filteredReminders].sort((a, b) => {
    if (!sortField) return 0;
    const valueA = a[sortField];
    const valueB = b[sortField];

    if (sortOrder === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Gestion de la modale
  const showModal = async (action, reminder) => {
    setModalAction(action);
    setSelectedReminder(reminder);

    if (action === "resend") {
      try {
        const response = await Axios.get(
          `/getOwnerProfile/${reminder.user_id}`
        );
        setUserProfile(response.data.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération du profil utilisateur :",
          error.message
        );
        setUserProfile(null);
      }
    }

    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
    setSelectedReminder(null);
    setModalAction(null);
  };

  const handleDelete = async () => {
    if (selectedReminder) {
      const { cabid, user_id, Eoutpout } = selectedReminder;
      try {
        const response = await Axios.post("/deleteReminderEmail", {
          cabid,
          userId: user_id,
          Eoutpout,
        });

        if (response.data.success) {
          setReminders((prevReminders) =>
            prevReminders.filter(
              (reminder) =>
                reminder.cabid !== cabid ||
                reminder.user_id !== user_id ||
                reminder.Eoutpout !== Eoutpout
            )
          );
        } else {
          console.error("Erreur : ", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors de la suppression :", error.message);
      } finally {
        hideModal();
      }
    }
  };

  const handleResend = async () => {
    if (selectedReminder) {
      const { messageId, cabid, user_id, Eoutpout } = selectedReminder;
      try {
        // Renvoi par messageId si disponible, sinon par clé métier
        // (permet de relancer les rappels en échec dont le messageId est null)
        const payload = messageId
          ? { messageId }
          : { cabid, userId: user_id, Eoutpout };
        const response = await Axios.post("/resendReminderEmail", payload);

        if (response.data.success) {
          console.log("Email renvoyé avec succès :", response.data.message);
          toast.success(t("reminders.resend_success"));
        } else {
          console.error(
            "Erreur lors du renvoi de l'email :",
            response.data.message
          );
          toast.error(t("reminders.resend_error"));
        }
      } catch (error) {
        console.error("Erreur lors du renvoi de l'email :", error.message);
        toast.error(t("reminders.resend_error"));
      } finally {
        hideModal();
      }
    }
  };

  const confirmAction = () => {
    if (modalAction === "delete") {
      handleDelete();
    } else if (modalAction === "resend") {
      handleResend();
    } else if (modalAction === "clean") {
      handleCleanObsolete();
    }
  };

 
  if (error) {
    return <p>{error}</p>;
  }

  return (
    <>
    <div className={styles.container}>
      <h3>{t("calibration.reminders_title")}</h3>
      <p className={styles.infoNote}>{t("reminders.brevo_window_note")}</p>
      <div className={styles.filters}>
        <input
          type="text"
          placeholder={t("common.search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <select
          value={filterReminderType}
          onChange={(e) => setFilterReminderType(e.target.value)}
          className={styles.selectInput}
        >
          <option value="all">{t("reminders.all_reminder_types")}</option>
          <option value="firstReminder">First Reminder</option>
          <option value="secondReminder">Second Reminder</option>
          <option value="thirdReminder">Third Reminder</option>
          <option value="overdueReminder">Overdue Reminder</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={styles.selectInput}
        >
          <option value="all">{t("reminders.all_statuses")}</option>
          <option value="hardBounces">Hard Bounces</option>
          <option value="softBounces">Soft Bounces</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
        </select>
        <PermissionGate actionKey={"ManageCertificat.remindCalibrationEmail"}>
          <button
            type="button"
            onClick={handleRefreshStatuses}
            disabled={refreshingStatuses}
            className={styles.resendButton}
          >
            {refreshingStatuses
              ? t("common.loading")
              : t("reminders.refresh_statuses")}
          </button>
        </PermissionGate>
      </div>
      {refreshingStatuses && (
        <div className={styles.refreshProgress}>
          <div className={styles.refreshProgressLabel}>
            {refreshProgress && refreshProgress.total > 0
              ? t("reminders.refresh_statuses_progress", {
                  processed: refreshProgress.processed,
                  total: refreshProgress.total,
                })
              : t("reminders.refresh_statuses_preparing")}
          </div>
          <div className={styles.refreshProgressBar}>
            <div
              className={styles.refreshProgressFill}
              style={{
                width:
                  refreshProgress && refreshProgress.total > 0
                    ? `${Math.round(
                        (refreshProgress.processed / refreshProgress.total) * 100
                      )}%`
                    : "0%",
              }}
            />
          </div>
          {refreshProgress && refreshProgress.total > 0 && (
            <div className={styles.refreshProgressPercent}>
              {Math.round(
                (refreshProgress.processed / refreshProgress.total) * 100
              )}
              %
            </div>
          )}
        </div>
      )}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th onClick={() => handleSort("id")}>ID</th>
              <th onClick={() => handleSort("owner_login")}>{t("reminders.center_name")}</th>
              <th onClick={() => handleSort("cabname")}>{t("calibration.cabin")}</th>
              <th onClick={() => handleSort("user_name")}>{t("reminders.user_name")}</th>
              <th onClick={() => handleSort("user_email")}>{t("reminders.user_email")}</th>
              <th onClick={() => handleSort("Eoutpout")}>{t("reminders.transducer")}</th>
              <th onClick={() => handleSort("reminderType")}>
                {t("reminders.reminder_type")}
              </th>
              <th>{t("reminders.email_status")}</th>
              <th onClick={() => handleSort("created_at")}>{t("reminders.created")}</th>
              <th onClick={() => handleSort("updated_at")}>{t("reminders.updated_at")}</th>
              <th>{t("calibration.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedReminders.map((reminder) => (
              <tr key={reminder.id}>
                <td>{reminder.id}</td>
                <td>{reminder.owner_login}</td>
                <td>{reminder.cabname}</td>
                <td>{reminder.user_name}</td>
                <td>{reminder.user_email}</td>
                <td>{reminder.Eoutpout}</td>
                <td>{reminder.reminderType}</td>
                <td>{emailStatuses[reminder.messageId] || t("reminders.waiting")}</td>
                <td>{new Date(reminder.created_at).toLocaleString()}</td>
                <td>{new Date(reminder.updated_at).toLocaleString()}</td>
                <td>
                  <div className={styles.actions}>
                    {/* <button
                      onClick={() => showModal("delete", reminder)}
                      className={styles.deleteButton}
                    >
                      Supprimer
                    </button> */}
                    <PermissionGate actionKey={"ManageCertificat.remindCalibrationEmail"}>
                    <button
                      onClick={() => showModal("resend", reminder)}
                      className={styles.resendButton}
                    >
                      {t("common.resend")}
                    </button>
                    </PermissionGate>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {/* Modale */}
      <Modal isShowing={isModalVisible} hide={hideModal}>
        <div className={styles.containerModal}>
          {modalAction === "clean" && (
            <div>
              <p>{t("reminders.confirm_clean")}</p>
            </div>
          )}
          {selectedReminder && (
            <div>
              <p>
                <strong>{t("reminders.cabin_name_label")}</strong> {selectedReminder.cabname}
              </p>
              <p>
                <strong>{t("reminders.username_label")}</strong> {selectedReminder.user_name}
              </p>
              <p>
                <strong>{t("reminders.previous_email_label")}</strong>{" "}
                {selectedReminder.user_email}
              </p>
              <p>
                <strong>{t("reminders.current_email_label")}</strong>{" "}
                {userProfile ? userProfile.user_email : t("common.loading")}
              </p>
              <p>
                <strong>{t("reminders.transducer_label")}</strong> {selectedReminder.Eoutpout}
              </p>
              <p>
                {modalAction === "delete" ? t("reminders.confirm_delete") : t("reminders.confirm_resend")}
              </p>
            </div>
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={hideModal}
              className={styles.deleteButton}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={confirmAction}
              className={styles.resendButton}
            >
              {t("common.confirm")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReminderLateCalibration;